"use client";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import icon from "../../public/aiproperty-logo.png";
import ai from "../../public/ai-icon.png";
import smily from "../../public/emoji-pink.png";
import msg from "../../public/send-msg.png";
import { Move, MessageCircle, X as XIcon } from "lucide-react";
import down from "../../public/down-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/lib/redux/store";
import {
  closeChatBot,
  getChatHistory,
  getUserByIp,
  GuestFormDataPayload,
  handleChatBot,
  openChatBot,
  sendChatBotFormData,
} from "@/lib/redux/slice/chatbotSlice";
import { useRouter } from "next/navigation";
import link from "../../public/pvt-space-two.png";
import { getIPAddress } from "@/lib/redux/slice/authSlice";
import ChatBotLoader from "./ChatBotLoader";
import {
  ChatBotPropertyDetails,
  Property,
} from "@/interfaces/propertyinterface";
import { formatProjectName, formatPropertyName } from "@/lib/utils";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import {
  guestFormValidation,
  GuestFormValidationType,
} from "@/validations/guestFormSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { IoLogoWhatsapp } from "react-icons/io5";

// v2 chatbot: tool-calling agent grounded in our DB. Guest-form / IP /
// chat-history flow from v1 is bypassed — bot is stateless, history is
// kept in component state for the session.
const CHATBOT_ENABLED = true;

// ---------------------------------------------------------------------------
// Chat markdown rendering. The bot replies in a small markdown subset:
// ### headings, **bold**, bullet/numbered lists, [text](url) links and bare
// URLs (the backend system prompt cites listings as plain URLs on their own
// line). This hand-rolled renderer builds React elements directly, so model
// output only ever passes through React's own text escaping — no innerHTML,
// no sanitiser needed. Links to our own /single-property/ pages render as a
// tappable card instead of a bare link so the chat feeds the funnel.

const PROPERTY_PATH_RE = /\/single-property\/([^\s)?#]+)/;

function isPropertyUrl(url: string): boolean {
  return /aiproperty\.sg/i.test(url) && PROPERTY_PATH_RE.test(url);
}

function slugToDisplayName(slug: string): string {
  return decodeURIComponent(slug)
    .replace(/-/g, " ")
    .replace(/\b[a-z]/g, (c) => c.toUpperCase())
    .trim();
}

function PropertyLinkCard({ url, label }: { url: string; label?: string }) {
  const router = useRouter();
  const slug = url.match(PROPERTY_PATH_RE)?.[1] ?? "";
  // Prefer the markdown label as the display name when the model gave one
  // (and it isn't just the URL repeated); otherwise reconstruct it from the slug.
  const name =
    label && !/^https?:\/\//.test(label) ? label : slugToDisplayName(slug);
  if (!slug) return null;
  return (
    <button
      type="button"
      onClick={() =>
        router.push(
          `/single-property/${formatPropertyName(decodeURIComponent(slug))}`
        )
      }
      className="mt-1.5 flex w-full items-center justify-between gap-3 rounded-lg border border-line bg-white px-3 py-2 text-left shadow-sm transition hover:border-brand-500"
    >
      <span className="text-[13px] font-semibold text-ink">{name}</span>
      <span className="whitespace-nowrap text-[12px] font-medium text-brand-600">
        View project →
      </span>
    </button>
  );
}

const INLINE_TOKEN_RE =
  /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s)]+)|\*\*([^*]+)\*\*/g;

// Render one line's inline content. Property-page links are pulled out of the
// text flow and returned separately as cards so they sit below the sentence.
function renderInline(text: string, keyPrefix: string) {
  const nodes: React.ReactNode[] = [];
  const cards: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  INLINE_TOKEN_RE.lastIndex = 0;
  while ((match = INLINE_TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const k = `${keyPrefix}-${key++}`;
    if (match[1] && match[2]) {
      // [label](url)
      if (isPropertyUrl(match[2])) {
        cards.push(
          <PropertyLinkCard key={k} url={match[2]} label={match[1]} />
        );
      } else {
        nodes.push(
          <a
            key={k}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#FFCC00]"
          >
            {match[1]}
          </a>
        );
      }
    } else if (match[3]) {
      // bare URL — keep trailing sentence punctuation out of the link
      const url = match[3].replace(/[.,;:!?]+$/, "");
      const trailing = match[3].slice(url.length);
      if (isPropertyUrl(url)) {
        cards.push(<PropertyLinkCard key={k} url={url} />);
      } else {
        nodes.push(
          <a
            key={k}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline break-all hover:text-[#FFCC00]"
          >
            {url}
          </a>
        );
      }
      if (trailing) nodes.push(trailing);
    } else if (match[4]) {
      // **bold**
      nodes.push(<strong key={k}>{match[4]}</strong>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return { nodes, cards };
}

function ChatMarkdown({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: "ul" | "ol" | null = null;
  let key = 0;

  const flushList = () => {
    if (listType && listItems.length > 0) {
      blocks.push(
        listType === "ul" ? (
          <ul key={`b${key++}`} className="mt-1 list-disc space-y-1 pl-4 first:mt-0">
            {listItems}
          </ul>
        ) : (
          <ol key={`b${key++}`} className="mt-1 list-decimal space-y-1 pl-4 first:mt-0">
            {listItems}
          </ol>
        )
      );
    }
    listType = null;
    listItems = [];
  };

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }

    const heading = line.match(/^#{1,4}\s+(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);

    if (heading) {
      flushList();
      const { nodes, cards } = renderInline(heading[1], `h${key}`);
      blocks.push(
        <p key={`b${key++}`} className="mt-2 text-[14px] font-bold leading-snug first:mt-0">
          {nodes}
        </p>
      );
      blocks.push(...cards);
    } else if (bullet || numbered) {
      const type = bullet ? "ul" : "ol";
      if (listType !== type) flushList();
      listType = type;
      const { nodes, cards } = renderInline((bullet ?? numbered)![1], `li${key}`);
      listItems.push(
        <li key={`li${key++}`}>
          {nodes}
          {cards}
        </li>
      );
    } else {
      flushList();
      const { nodes, cards } = renderInline(line, `p${key}`);
      if (nodes.length > 0) {
        blocks.push(
          <p key={`b${key++}`} className="mt-1.5 first:mt-0">
            {nodes}
          </p>
        );
      }
      blocks.push(...cards);
    }
  }
  flushList();
  return <div className="text-[13px] leading-relaxed">{blocks}</div>;
}

interface EmojiObject {
  native: string;
}

interface ChatBot {
  auth: string | null;
  question: string;
  IP: string | null;
}

export interface ChatHistory {
  IP: string | null;
  email: string | null; // keep as string | null
}

interface ChatMessage {
  sender: string | "bot";
  message: string;
  properties: ChatBotPropertyDetails[];
  timestamp?: string;
}

interface ChatItem {
  req: string;
  res: {
    properties: Property[];
    response: string;
  };
  timestamp: string;
}

interface GuestFormData {
  name: string;
  email: string;
  mobile: string;
}

export default function ChatBox() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GuestFormValidationType>({
    resolver: yupResolver(guestFormValidation),
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { loading, chatHistoryLoader, isOpen } = useSelector(
    (state: RootState) => state.chatbot
  );
  const [isDragging, setIsDragging] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [message, setMessage] = useState("");
  const [ip, setIp] = useState<string>("");

  // Greeting shown for fresh sessions — kept in a const so we can re-use it
  // when the user explicitly clears history.
  const GREETING: ChatMessage = {
    sender: "bot",
    message:
      "Hi! I'm the AI Property assistant. Ask me about any new launch in Singapore — district, price, tenure, available units, anything. If you'd rather talk to a human, tap 'Talk to agent' at the top.",
    properties: [],
  };

  // Hydrate chat history from localStorage on mount. Cap to last 50 turns to
  // keep size sensible (each turn ~0.5KB, 50 = ~25KB, well under the 5MB
  // localStorage budget). Survives page refresh + browser close/reopen.
  const STORAGE_KEY = "aiproperty.chat.history.v1";
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>(() => {
    if (typeof window === "undefined") return [GREETING];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [GREETING];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {}
    return [GREETING];
  });

  // Persist on every change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const trimmed = chatHistory.slice(-50);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {}
  }, [chatHistory]);

  const clearChatHistory = () => {
    setChatHistory([GREETING]);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };
  // v2: skip guest gate entirely — anyone can chat without giving details.
  // Lead capture happens via the existing enquiry form / auto-popup.
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [isUserExist, setIsUserExist] = useState(true);
  const [guestFormData, setGuestFormData] = useState<GuestFormData>({
    name: "",
    email: "",
    mobile: "",
  });
  const chatboxRef = useRef<HTMLDivElement>(null);
  const offset = useRef({ x: 0, y: 0 });
  const hasFetchedChatHistory = useRef(false); // To prevent multiple fetches

  // v2 chatbot is stateless. We skip:
  // - IP fetch (no longer used for guest identity)
  // - guest-user fetch (dead python endpoint)
  // - chat-history fetch (dead python endpoint)
  // The chat opens immediately on click. Lead capture goes through the
  // existing EnquiryForm + WhatsApp handoff in this component.

  // History persists across close/reopen via localStorage. No reset on close.

  // Scroll to bottom when chat history updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const fetchGuestUser = async () => {
    try {
      const response = await dispatch(getUserByIp({ ip }));
      if (response?.payload?.success) {
        setShowGuestForm(false);
        setIsUserExist(true);
        setGuestFormData({
          name: response.payload?.user?.name,
          email: response.payload?.user?.email,
          mobile: response.payload?.user?.mobile,
        });
      } else if (!token) {
        setShowGuestForm(true);
        setIsUserExist(false);
      }
    } catch (error) {
      console.log("error", error);
      if (!token) {
        setShowGuestForm(true);
      }
    }
  };

  const fetchChatHistory = async () => {
    try {
      // ---------- FIX: ensure email is string | null (never undefined) ----------
      const data: ChatHistory = {
        IP: token ? null : ip ?? null,
        email: user?.email ?? null, // <-- null-coalescing to avoid `undefined`
      };

      const response = await dispatch(getChatHistory(data));
      const history = response?.payload?.history?.flatMap((item: ChatItem) => [
        {
          sender: "user",
          message: item.req,
          timestamp: item.timestamp,
          properties: [],
        },
        {
          sender: "bot",
          message: item.res.response,
          timestamp: item.timestamp,
          properties: item.res.properties,
        },
      ]);
      setChatHistory((prev) => [
        {
          sender: "bot",
          message: `Hi ${guestFormData?.name || user?.name || "Guest"}, How can I help you?`,
          properties: [],
        },
        ...(history || []),
      ]);
    } catch (error) {
      console.error("Failed to fetch chat history:", error);
    }
  };

  const toggleChatbox = () => {
    if (!isOpen) {
      // v2: open chat immediately, no guest gate.
      setShowGuestForm(false);
      dispatch(openChatBot());
    } else {
      // v2: keep history on close — persisted to localStorage.
      dispatch(closeChatBot());
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (chatboxRef.current) {
      setIsDragging(true);
      const rect = chatboxRef.current.getBoundingClientRect();
      offset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && chatboxRef.current) {
      const maxX = window.innerWidth - chatboxRef.current.offsetWidth;
      const maxY = window.innerHeight - chatboxRef.current.offsetHeight;
      let newX = e.clientX - offset.current.x;
      let newY = e.clientY - offset.current.y;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      chatboxRef.current.style.left = `${newX}px`;
      chatboxRef.current.style.top = `${newY}px`;
      chatboxRef.current.style.bottom = "auto";
      chatboxRef.current.style.right = "auto";
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const addEmoji = (emoji: EmojiObject) => {
    setMessage(message + emoji.native);
    setShowEmojiPicker(false);
  };

  const handleSendMessage = async () => {
    if (message.trim() === "") return;

    const userMessage = message;
    setChatHistory((prev) => [
      ...prev,
      {
        sender: "user",
        message: userMessage,
        properties: [],
        timestamp: new Date().toISOString(),
      },
    ]);
    setMessage("");

    // Build last 10 turns of history (user/assistant only, skip the
    // greeting bot message and any rendered loading states).
    const history = chatHistory
      .filter(
        (m) =>
          m.message &&
          m.message !== "Hi, How can I help you?" &&
          !m.message.startsWith("Hi ")
      )
      .slice(-10)
      .map((m) => ({
        role: m.sender === "bot" ? ("assistant" as const) : ("user" as const),
        content: m.message,
      }));

    try {
      const response = await dispatch(
        handleChatBot({ question: userMessage, history })
      );
      const payload = response?.payload as { response?: string } | undefined;
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "bot",
          message:
            payload?.response ||
            "Sorry, I had trouble with that. Try rephrasing?",
          properties: [],
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.log("error", error);
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "bot",
          message: "Sorry, please try again in a few seconds.",
          properties: [],
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleGuestFormSubmit: SubmitHandler<FieldValues> = async (data) => {
    try {
      const payload: GuestFormDataPayload = {
        name: data?.name,
        email: data?.email,
        phone: data?.mobile,
        IP: ip,
      };
      const response = await dispatch(sendChatBotFormData(payload));
      if (response?.payload?.success) {
        setShowGuestForm(false);
        setIsUserExist(true);
        setGuestFormData({
          name: data.name,
          email: data.email,
          mobile: data.mobile,
        });
        reset();
        setChatHistory([
          {
            sender: "bot",
            message: `Hi ${data.name}, How can I help you?`,
            properties: [],
          },
        ]);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleViewProperty = (name: string) => {
    const formattedName = formatPropertyName(name);
    router.push(`/single-property/${formattedName}`);
  };

  return (
    <div className="relative z-30">
      <div className="aip-chat-fab fixed md:bottom-8 bottom-3 md:right-6 right-3 flex items-center gap-3">
        {/* WhatsApp Button */}
        <a
          href="https://wa.me/6588772688"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp Winnie"
          className="h-[3.75rem] w-[3.75rem] rounded-full bg-[#24D366] hover:bg-green-700 text-white flex items-center justify-center shadow-lg transition-all hover:scale-105"
        >
          <IoLogoWhatsapp size={36} />
        </a>

        {/* AI Chat Button — branded "AI" mark + online pulse */}
        {CHATBOT_ENABLED && (
          <button
            onClick={toggleChatbox}
            aria-label="Open AI Property assistant"
            className="h-[3.75rem] w-[3.75rem] rounded-full bg-[#0094FF] hover:bg-[#0080E0] text-white flex items-center justify-center shadow-lg transition-all hover:scale-105 relative"
          >
            <span className="text-[20px] font-bold tracking-tight leading-none">AI</span>
            <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-[#22C55E] border-2 border-white" />
          </button>
        )}
      </div>

      {CHATBOT_ENABLED && isOpen && (
        <div
          ref={chatboxRef}
          className="fixed bottom-20 md:right-5 right-0 md:w-96 w-full bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          {chatHistoryLoader && <ChatBotLoader />}
          <div className="bg-[#E6F4FF] border-b border-[#B3DCFF] p-3 rounded-t-2xl flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-9 w-9 rounded-full bg-[#0094FF] text-white flex items-center justify-center flex-shrink-0">
                <MessageCircle size={20} strokeWidth={2.25} />
              </div>
              <div className="leading-tight min-w-0">
                <p className="text-[14px] font-bold text-[#120F30] truncate">
                  AI Property Specialist
                </p>
                <p className="text-[11px] text-[#3A3A3A]">Online · replies instantly</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <a
                href={`https://wa.me/6588772688?text=${encodeURIComponent(
                  "Hi Winnie, I was chatting on aiproperty.sg and wanted to speak with you directly."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium rounded-full bg-[#24D366] text-white hover:bg-green-700 transition"
              >
                <IoLogoWhatsapp className="h-3.5 w-3.5" />
                Talk to Agent
              </a>
              <button
                aria-label="Clear chat"
                title="Clear chat"
                onClick={clearChatHistory}
                className="text-[11px] text-[#3A3A3A] hover:text-[#120F30] underline px-1"
              >
                Clear
              </button>
              <button
                aria-label="Close chat"
                className="h-8 w-8 rounded-full hover:bg-white/60 flex items-center justify-center transition"
                onClick={toggleChatbox}
              >
                <XIcon className="h-5 w-5 text-[#120F30]" />
              </button>
            </div>
          </div>

          {showGuestForm && !token && !isUserExist ? (
            <div className="p-5 space-y-4">
              <h3 className="text-[#0094FF] text-center">
                Please provide your details to start chatting
              </h3>
              <form
                className="space-y-4"
                onSubmit={handleSubmit(handleGuestFormSubmit)}
              >
                <div>
                  <input
                    type="text"
                    {...register("name")}
                    placeholder="Enter your name"
                    className="w-full py-2 px-5 rounded-full border border-[#0094FF] outline-none text-black"
                  />
                  {errors.name && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="email"
                    {...register("email")}
                    placeholder="Enter your email"
                    className="w-full py-2 px-5 rounded-full border border-[#0094FF] outline-none text-black"
                  />
                  {errors.email && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    {...register("mobile")}
                    placeholder="Enter your mobile number"
                    className="w-full py-2 px-5 rounded-full border border-[#0094FF] outline-none text-black"
                  />
                  {errors.mobile && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2 px-5 bg-[#0094FF] text-white rounded-full"
                >
                  {isSubmitting ? "Signing in..." : "Submit"}
                </button>
              </form>
            </div>
          ) : (
            (token || isUserExist) && (
              <>
                <div
                  className="p-5 space-y-4 max-h-96 overflow-y-auto custom-scrollbar relative"
                  ref={scrollRef}
                >
                  {chatHistory?.map((chat, index) => (
                    <div
                      key={index}
                      className={`flex flex-col items-start relative ${chat.sender === "user" ? "items-end" : ""
                        }`}
                    >
                      {chat.sender === "bot" && (
                        <Image
                          src="/winnie.jpg"
                          alt="Winnie"
                          width={30}
                          height={30}
                          className="rounded-full object-cover absolute"
                        />
                      )}
                      <div
                        className={`ml-10 mr-10 py-2 px-3 rounded-lg ${chat.sender === "user"
                            ? "bg-[#121212] rounded-tr-none"
                            : "bg-[#0094FF] rounded-tl-none"
                          } text-white max-w-[70%]`}
                      >
                        <ChatMarkdown text={chat.message} />
                      </div>
                      {chat.sender === "user" && (
                        <Image
                          src={
                            user &&
                              user.images &&
                              user.images[0] &&
                              user.images[0].path
                              ? `${process.env.NEXT_PUBLIC_API_URL}${user.images[0].path}`
                              // Generic grey user silhouette when the visitor
                              // isn't logged in. Neutral, doesn't imply any
                              // particular identity.
                              : "/user-avatar-grey.svg"
                          }
                          alt="User Avatar"
                          width={30}
                          height={30}
                          className="rounded-full object-cover absolute"
                        />
                      )}
                      {chat.sender === "bot" && chat?.properties?.length > 0 && (
                        <div className="mt-4 space-y-2 w-full">
                          {chat?.properties?.map((property, propertyIndex) => {
                            const imageUrl =
                              property?.images?.length > 0
                                ? `${process.env.NEXT_PUBLIC_IMAGE_URL}/${formatProjectName(property?.name)}/property_images/${property?.images[0]}`
                                : link;


                            return (
                              <div
                                key={propertyIndex}
                                className="flex border p-1 rounded-md bg-white shadow-sm"
                              >
                                <Image
                                  src={imageUrl}
                                  alt={`Property ${propertyIndex}`}
                                  width={52}
                                  height={39}
                                  className="object-contain"
                                />
                                <div className="text-black text-[12px] p-1">
                                  <p>{property?.location?.address}</p>
                                  <p
                                    className="text-[#0094FF] cursor-pointer"
                                    onClick={() => handleViewProperty(property?.name)}
                                  >
                                    View Property
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {loading && (
                  <div className="flex items-center ml-2 mr-2 py-2 px-3">
                    <div className="dot-pulse">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                )}
                <div className="p-4 bg-white rounded-b-2xl flex items-center relative gap-2">
                  <button
                    className=""
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Image src={smily} alt="" width={30} height={30} />
                  </button>
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message"
                    className="flex-1 py-2 px-5 rounded-full border border-[#0094FF] outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="absolute right-6 top-1/2 transform -translate-y-1/2"
                    onClick={handleSendMessage}
                  >
                    <Image src={msg} alt="" width={20} height={20} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full right-0 mb-2 w-full m-auto flex justify-center items-center">
                      <Picker data={data} onEmojiSelect={addEmoji} />
                    </div>
                  )}
                </div>
              </>
            )
          )}

          {/* Persistent human-handoff footer */}
          <a
            href="https://wa.me/6588772688"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 border-t border-line bg-surface px-4 py-2 text-[11px] text-ink-muted transition hover:text-ink"
          >
            <IoLogoWhatsapp className="h-3.5 w-3.5 text-[#24D366]" />
            <span>Prefer a human? WhatsApp Winnie</span>
          </a>
        </div>
      )}
    </div>
  );
}
